import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Toolbar, List, Icon, useTheme, ListItemButton, Divider, Avatar, Typography, Button, Slide, IconButton } from "@mui/material";
import { PlayArrow } from "@mui/icons-material";
import { BrowserView, isBrowser, isMobile, MobileView } from "react-device-detect";
import { useUser } from "../hook/useUser";

const AppSidebar = ({ open, setOpen, setShowMyProfile, headerHeight }) => {
  const navigate = useNavigate();
  const { moduleName, projectId } = useParams();
  const theme = useTheme();
  const { user } = useUser(projectId);
  const [projectModules, setProjectModules] = useState([
    {
      name: "Project",
      labelName: "Project Home",
      icon: <img src={"assets/icons/home.svg"} style={{ height: 20 }} alt={"home"}></img>,
      disabled: !projectId,
    },
    {
      name: "3DModel",
      labelName: "3D Model",
      icon: <img src={"assets/icons/model.svg"} style={{ height: 20 }} alt={"3DModel"}></img>,
      disabled: !projectId,
    },
    {
      name: "Schedule",
      labelName: "Schedule",
      icon: <img src={"assets/icons/schedule.svg"} style={{ height: 20 }} alt={"schedule"}></img>,
      disabled: !projectId,
    },
    {
      name: "Safety",
      labelName: "Safety",
      icon: <img src={"assets/icons/safety.svg"} style={{ height: 20 }} alt={"safety"}></img>,
      disabled: !projectId,
    },
    {
      name: "Quantity",
      labelName: "Quantity",
      icon: <img src={"assets/icons/qto.svg"} style={{ height: 20 }} alt={"quantity"}></img>,
      disabled: !projectId,
    },
    {
      name: "Observation",
      labelName: "Observation",
      icon: <img src={"assets/icons/observation.svg"} style={{ height: 20 }} alt={"observation"}></img>,
      disabled: !projectId,
    },
    {
      name: "SitePhoto",
      labelName: "Site Photo",
      icon: <img src={"assets/icons/photo.svg"} style={{ height: 20 }} alt={"sitephoto"}></img>,
      disabled: !projectId,
    },
  ]);

  const commonModules = [
    {
      name: "Portfolio",
      labelName: "Portfolio",
      icon: <img src={"assets/icons/projects.svg"} style={{ height: 20 }} alt={"portfolio"}></img>,
    },
    ...(user?.userRole === "System Administrator"
      ? [
          {
            name: "SystemAdmin",
            labelName: "System Admin",
            icon: <img src={"assets/icons/twopeople.svg"} style={{ height: 20 }} alt={"systemAdmin"}></img>,
          },
        ]
      : []),
  ];

  const mobileModules = [
    {
      name: "Portfolio",
      labelName: "My Projects",
      icon: <img src={"assets/icons/home.svg"} style={{ height: 20 }} alt={"home"}></img>,
    },
    {
      name: "3DModel",
      labelName: "Model",
      icon: <img src={"assets/icons/model.svg"} style={{ height: 20 }} alt={"3DModel"}></img>,
      disabled: !projectId,
    },
    {
      name: "Observation",
      labelName: "Observation",
      icon: <img src={"assets/icons/observation.svg"} style={{ height: 20 }} alt={"observation"}></img>,
      disabled: !projectId,
    },
    {
      name: "SitePhoto",
      labelName: "Site Photo",
      icon: <img src={"assets/icons/photo.svg"} style={{ height: 20 }} alt={"sitephoto"}></img>,
      disabled: !projectId,
    },
  ];

  useEffect(() => {
    setProjectModules([
      {
        name: "Project",
        labelName: "Project Home",
        icon: <img src={"assets/icons/home.svg"} style={{ height: 20 }} alt={"home"}></img>,
        disabled: !projectId,
      },
      {
        name: "3DModel",
        labelName: "3D Model",
        icon: <img src={"assets/icons/model.svg"} style={{ height: 20 }} alt={"3DModel"}></img>,
        disabled: !projectId,
      },
      {
        name: "Schedule",
        labelName: "Schedule",
        icon: <img src={"assets/icons/schedule.svg"} style={{ height: 20 }} alt={"schedule"}></img>,
        disabled: !projectId,
      },
      ...(user?.costAccess
        ? [
            {
              name: "Cost",
              labelName: "Cost",
              icon: <img src={"assets/icons/cost.svg"} style={{ height: 20 }} alt={"cost"}></img>,
              disabled: !projectId,
            },
          ]
        : []),
      {
        name: "Safety",
        labelName: "Safety",
        icon: <img src={"assets/icons/safety.svg"} style={{ height: 20 }} alt={"safety"}></img>,
        disabled: !projectId,
      },
      {
        name: "Quantity",
        labelName: "Quantity",
        icon: <img src={"assets/icons/qto.svg"} style={{ height: 20 }} alt={"quantity"}></img>,
        disabled: !projectId,
      },
      ...(user?.reportModule
        ? [
            {
              name: "Report",
              labelName: "Report",
              icon: <img src={"assets/icons/report.svg"} style={{ height: 20 }} alt={"report"}></img>,
              disabled: !projectId,
            },
          ]
        : []),
      {
        name: "Observation",
        labelName: "Observation",
        icon: <img src={"assets/icons/observation.svg"} style={{ height: 20 }} alt={"observation"}></img>,
        disabled: !projectId,
      },
      {
        name: "SitePhoto",
        labelName: "Site Photo",
        icon: <img src={"assets/icons/photo.svg"} style={{ height: 20 }} alt={"sitephoto"}></img>,
        disabled: !projectId,
      },
      ...(user?.userRole !== "System Administrator"
        ? [
            {
              name: "ProjectAdmin",
              labelName: "Project Admin",
              icon: <img src={"assets/icons/onepeople.svg"} style={{ height: 20 }} alt={"projectAdmin"}></img>,
              disabled: !projectId,
            },
          ]
        : []),
    ]);
  }, [user, projectId]);

  const handleModuleClick = (name) => {
    if (isMobile) {
      setOpen(false);
    }
    if (name === "Portfolio") {
      navigate(`/modules/Portfolio`);
    } else if (name === "SystemAdmin") {
      navigate(`/modules/SystemAdmin`);
    } else if (projectId) {
      navigate(`/modules/${name}/${projectId}`);
    } else {
      navigate(`/modules/${name}`);
    }
  };

  const logout = () => {
    window.localStorage.removeItem("token");
    window.localStorage.removeItem("refreshToken");
    window.localStorage.removeItem("exp");
    navigate(`/`);
  };

  const sideBar = (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: 205,
        height: "100%",
        zIndex: 1000,
        background: `linear-gradient(360deg,#fff 0%, #008AD8 40%, ${theme.palette.primary.main} 100%)`,
        boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        textAlign: "center",
      }}
    >
      <Toolbar sx={{ minHeight: headerHeight }} />
      <MobileView>
        <Box display="flex" alignItems="center" gap={1} width="100%" p={1} sx={{ backgroundColor: "white" }}>
          <IconButton onClick={() => setShowMyProfile(true)}>
            <Avatar sx={{ width: 30, height: 30, bgcolor: theme.palette.primary.main }}>
              {user?.firstName?.charAt(0)}
              {user?.lastName?.charAt(0)}
            </Avatar>
          </IconButton>
          <Typography>{user?.email}</Typography>
        </Box>
      </MobileView>
      <List
        sx={{
          p: 3,
          pt: isBrowser ? 3 : 1,
        }}
      >
        {(isBrowser ? commonModules : []).map((module, index) => (
          <ListItemButton
            onClick={() => handleModuleClick(module.name)}
            key={module.labelName}
            sx={{
              display: "flex",
              py: 1.5,
              px: 0,
            }}
          >
            <Icon sx={{ color: "#fff" }}>{module.icon}</Icon>
            <Box
              component="span"
              sx={{
                fontWeight:
                  moduleName === module.name ||
                  (["UserManagement", "UserDetails"].includes(moduleName) && module.labelName === "System Admin") ||
                  (["ProjectManagement", "ProjectDetails"].includes(moduleName) && module.labelName === "System Admin")
                    ? 600
                    : "normal",
                color: "#fff",
                ml: 2,
                fontSize: theme.typography.fontSize + 2,
              }}
            >
              {module.labelName}
              {(moduleName === module.name ||
                (["UserManagement", "UserDetails"].includes(moduleName) && module.labelName === "System Admin") ||
                (["ProjectManagement", "ProjectDetails"].includes(moduleName) && module.labelName === "System Admin")) && (
                <PlayArrow
                  sx={{
                    transform: "rotate(180deg)",
                    mb: "-5px",
                    ml: "4px",
                  }}
                />
              )}
            </Box>
          </ListItemButton>
        ))}
      </List>
      <BrowserView>
        <Box display="flex" justifyContent="center">
          <Divider width="80%" color="white" />
        </Box>
      </BrowserView>
      <List
        sx={{
          p: 3,
          pt: isBrowser ? 3 : 1,
          maxHeight: "calc(100vh - 76px - 120px - 150px)",
          overflow: "auto",
        }}
      >
        {(isBrowser ? projectModules : mobileModules).map((module, index) => (
          <ListItemButton
            onClick={() => handleModuleClick(module.name)}
            disabled={module.disabled}
            key={module.labelName}
            sx={{
              display: "flex",
              py: 1.5,
              px: 0,
            }}
          >
            <Icon sx={{ color: "#fff" }}>{module.icon}</Icon>
            <Box
              component="span"
              sx={{
                fontWeight: moduleName === module.name ? 600 : "normal",
                color: "#fff",
                ml: 2,
                fontSize: theme.typography.fontSize + 2,
              }}
            >
              {module.labelName}
              {moduleName === module.name && <PlayArrow sx={{ transform: "rotate(180deg)", mb: "-5px", ml: "4px" }}></PlayArrow>}
            </Box>
          </ListItemButton>
        ))}
      </List>
      <MobileView>
        <Button
          variant="contained"
          style={{
            position: "absolute",
            left: (205 - 100) / 2,
            bottom: 250,
            width: 100,
            borderRadius: 10,
            backgroundColor: "white",
            color: "#717273",
          }}
          onClick={logout}
        >
          Log out
        </Button>
      </MobileView>
      <img style={{ position: "absolute", left: 0, bottom: 0, width: "100%" }} alt="sidebar" src="assets/background/sidebarbg.png" />
    </Box>
  );

  return isMobile ? (
    <Slide direction="right" in={open}>
      {sideBar}
    </Slide>
  ) : (
    sideBar
  );
};

export default AppSidebar;
